// Exemple d'utilisation:
// debugLog('Ceci est un message de debug général');
// debugLogRef('SESSION UNIQUE', 'Ceci est un message de debug spécifique à la session unique');

// Pour activer toutes les refs:
// setActiveRefs([]);

// Pour n'activer que certaines refs:
// setActiveRefs(['SESSION UNIQUE', 'IMPORT_ADMIN']);

/**
 * Catégories de debug disponibles
 */
export const DEBUG_REFS = {
  SESSION_UNIQUE: "SESSION UNIQUE",
  IMPORT_ADMIN: "IMPORT_ADMIN",
  GENERAL: "GENERAL",
  // Ajoute tes refs ici au fur et à mesure
};

/**
 * Configuration du système de debug
 */
const DEBUG_CONFIG = {
  // Master switch depuis .env (true en dev, false en prod)
  enabled: import.meta.env.VITE_DEBUG_ENABLED == "true",

  // Tableau des refs à activer (vide = toutes activées)
  activeRefs: [
    // "GENERAL",
    // "SESSION UNIQUE",
    // 'IMPORT_ADMIN',
  ],
};

/**
 * Génère une couleur à partir d'une string
 */
function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = hash % 360;
  return `hsl(${hue}, 70%, 50%)`;
}

/**
 * Récupère l'origine de l'appel (fichier:ligne)
 */
function getCallerTrace() {
  const stack = new Error().stack;
  if (!stack) return "unknown";

  const lines = stack.split("\n");
  const utilityFunctions = ["getCallerTrace", "debugLog"];

  // Regex qui matche Chrome ET Firefox
  // Groupe 1: chemin pour Chrome (après "at"), Groupe 2: chemin pour Firefox (après "@")
  const stackRegex = /(?:at\s+(?:.*?\s+\()?([^)]+?):\d+:\d+\)?|@(.+?):\d+:\d+)/;

  // Trouver la première ligne pertinente
  const relevantLine = lines.slice(1).find((line) => {
    const trimmed = line.trim();
    return trimmed && !utilityFunctions.some((fn) => trimmed.includes(fn));
  });

  if (!relevantLine) return "unknown";

  // Extraire avec la regex unifiée
  const match = relevantLine.match(stackRegex);
  if (!match) return "unknown";

  // Le chemin est soit dans groupe 1 (Chrome) soit dans groupe 2 (Firefox)
  const filePath = (match[1] || match[2]).split("?")[0].split("#")[0];

  return filePath.split("/").pop();
}

/**
 * Récupère l'heure actuelle au format hh:mm:ss
 */
function getCurrentTime() {
  const now = new Date();
  return now.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Fonction de debug avec ref GENERAL par défaut
 *
 * @param {...any} args - Arguments à logger (comme console.log)
 *
 * @example
 * debugLog('User data retrieved for', username)
 * debugLog('Message simple', obj1, obj2)
 */
export function debugLog(...args) {
  debugLogRef("GENERAL", ...args);
}

/**
 * Fonction de debug avec ref personnalisée
 *
 * @param {string} ref - Référence de la catégorie
 * @param {...any} args - Arguments à logger (comme console.log)
 *
 * @example
 * debugLogRef('SESSION UNIQUE', 'Token reçu:', token)
 * debugLogRef('IMPORT_ADMIN', 'Validation du fichier')
 */
export function debugLogRef(ref, ...args) {
  // Si désactivé globalement, on fait rien
  if (!DEBUG_CONFIG.enabled) return;

  // Si des refs sont spécifiées et que celle-ci n'en fait pas partie, on ignore
  if (
    DEBUG_CONFIG.activeRefs.length > 0 &&
    !DEBUG_CONFIG.activeRefs.includes(ref)
  ) {
    return;
  }

  const color = stringToColor(ref);
  const trace = getCallerTrace();
  const time = getCurrentTime();

  console.log(
    `%c${time} %c${trace} %c[${ref}]`,
    "color: #888; font-size: 0.9em;",
    "color: gray; font-size: 0.9em;",
    `color: ${color}; font-weight: bold;`,
    ...args,
  );
}

/**
 * Helper pour activer/désactiver des refs à la volée dans la console
 */
export function setActiveRefs(refs = []) {
  DEBUG_CONFIG.activeRefs = refs;
  console.log("Active refs:", refs.length === 0 ? "ALL" : refs);
}

/**
 * Helper pour activer/désactiver le debug
 */
export function toggleDebug(enabled) {
  DEBUG_CONFIG.enabled = enabled;
  console.log("Debug:", enabled ? "ENABLED" : "DISABLED");
}
